import { motion } from 'framer-motion'
import React from 'react'
import { useGetUsers } from '../../../api/admin/useGetUsers'
import { Button, SelectInput, Table } from '@demandlocal/components'
import { createColumnHelper } from '@tanstack/react-table'
import { User } from '../../../lib/types/User'
import { ReactComponent as ActionDelete } from '../../../images/icons/action_delete.svg'
import { ReactComponent as ActionEdit } from '../../../images/icons/action_edit.svg'
import toast from 'react-hot-toast'
import * as portals from 'react-reverse-portal'
import { useDeleteUserById } from '../../../api/admin/useDeleteUserById'
import FiltersContext from '../../../components/Filters/FiltersContext'
import TextInput from '../../../components/FormElements/TextInput'
import { Outlet } from 'react-router-dom'
import { useNavigate } from 'react-router'
import dayjs from 'dayjs'
import { queryClient } from '../../../api/queryClient'

const columnHelper = createColumnHelper<User>()

const AdminUsers = (): JSX.Element => {
  const [filters, setFilters] = React.useState<Partial<User>>({})
  const filtersPortalNode = React.useMemo(() => portals.createHtmlPortalNode(), [])
  const { mutateAsync: deleteUserAsync } = useDeleteUserById()

  const navigate = useNavigate()

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('id', {
        header: () => 'ID',
      }),
      columnHelper.accessor('first_name', {
        header: () => 'First Name',
        cell: (info) => {
          return <div className={'flex flex-col gap-1'}>{info?.getValue()}</div>
        },
      }),
      columnHelper.accessor('last_name', {
        header: () => 'Last Name',
        cell: (info) => {
          return <div className={'flex flex-col gap-2'}>{info?.getValue()}</div>
        },
      }),
      columnHelper.accessor('email', {
        header: () => 'Email',
        cell: (info) => {
          return <div className={'flex flex-col gap-2'}>{info?.getValue()}</div>
        },
      }),
      columnHelper.accessor('role_name', {
        header: () => 'Role',
        cell: (info) => {
          return <div className={'flex flex-col gap-2'}>{info?.getValue()}</div>
        },
      }),
      columnHelper.accessor('last_login', {
        header: 'Last Login',
        cell: (info) =>
          info?.getValue() === null
            ? '-'
            : dayjs(info?.getValue()).format('MMM DD, YYYY, h:mA'),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: (info) => (
          <div className={'flex flex-row'}>
            <ActionDelete
              className={'cursor-pointer'}
              onClick={async () => {
                if (confirm('Are you sure you want to delete this user?')) {
                  const toastId = toast.loading('Deleting user...')
                  await deleteUserAsync({
                    id: info.row.original.id.toString(),
                  })
                  await queryClient.invalidateQueries(['admin', 'user'])
                  toast.success('User deleted successfully', { id: toastId })
                }
              }}
            />
            <ActionEdit
              stroke={'#0932C2'}
              className={'ml-4 cursor-pointer'}
              onClick={() => {
                navigate(`${info.row.original.id}/edit`)
              }}
            />
          </div>
        ),
      }),
    ],
    [deleteUserAsync, navigate],
  )

  return (
    <>
      <motion.div className={'flex flex-1 flex-col pb-8 font-Spartan'}>
        <div className={'flex flex-1 flex-col'}>
          <div className={'pb-4 flex justify-between'}>
            <div
              className={
                'font-Spartan font-bold text-white rounded-md text-blue-primary text-base'
              }>
              Users
            </div>
            <div className="flex gap-4">
              <portals.OutPortal node={filtersPortalNode} />
              <Button onClick={() => navigate('new')}>+ Add new user</Button>
            </div>
          </div>
          <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
            <TextInput
              name={'first_name'}
              placeholder={'First Name'}
              label={'First Name'}
            />
            <TextInput name={'last_name'} placeholder={'Last Name'} label={'Last Name'} />
            <TextInput name={'email'} placeholder={'Email'} label={'Email'} />
            <SelectInput
              name={'role_id'}
              label="Role"
              options={[
                { label: 'Super Admin', value: 1 },
                { label: 'Agency Admin', value: 2 },
                { label: 'User', value: 3 },
              ]}
            />
          </FiltersContext>
          <Table columns={columns} filters={filters} fetchFn={useGetUsers} />
        </div>
      </motion.div>
      <Outlet />
    </>
  )
}

export default AdminUsers
